import {IrrevocableTrust} from "src/EstateFlowchart/model/IrrevocableTrust";
import {ValidationError, ValidationErrorType} from "../ValidationTypes";
import {isFlowchartNameDuplicate, isValid} from "../ValidationUtils";
import {EstateFlowchartState, EstateItemTypes} from "../../model/EstateFlowchartTypes";
import {Will} from "../../model/Will";
import {Miscellaneous} from "../../model/Miscellaneous";

function isIrrevocableTrust(trust: EstateItemTypes): trust is IrrevocableTrust {
    return (trust as IrrevocableTrust).terminatesUponSpecificDeath !== undefined;
}
function isMiscellaneous(trust: EstateItemTypes): trust is Miscellaneous {
    return (trust as Miscellaneous).visualCardRepresentation !== undefined;
}


function isWill(trust: EstateItemTypes): trust is Will {
    return (trust as Will).willOwner !== undefined;
}

const validateRequiredField = (
    field: any,
    property: string,
    parent: string,
    errorMessage: string
): ValidationError | null => {
    if (!isValid(field)) {
        return {
            type: ValidationErrorType.REQUIRED_FIELDS,
            property,
            parent,
            error: errorMessage,
        };
    }
    return null;
};

const validateDuplicateField = (
    flowcharts: EstateFlowchartState[],
    field: any,
    property: string,
    parent: string,
    errorMessage: string
): ValidationError | null => {
    if(property === "flowchartName" && isFlowchartNameDuplicate(flowcharts, field)) {
        return {
            type: ValidationErrorType.DUPLICATE_FIELDS,
            property,
            parent,
            error: errorMessage,
        }
    }
    return null;
}


const validateBasicDetailsSection = (trust: EstateItemTypes, estateFlowcharts: EstateFlowchartState[], flowchartId: string, enableEstateFlowchartMVPRelease: boolean): ValidationError[] => {
    const validationErrors: ValidationError[] = [];

    const requiredFields = [
        { field: trust.flowchartName, property: 'flowchartName', error: "Flowchart Name is required." }
    ];

    if(!isMiscellaneous(trust)) {
        requiredFields.push(
            { field: trust.legalName, property: 'legalName', error: "Legal Name is required." },
            { field: trust.nickName, property: 'nickName', error: "Nickname is required." },
            { field: 'trustCreationDate' in trust ? trust.trustCreationDate : trust.creationDate, property: 'dateCreated', error: "Date Created is required." },
        );
    } else {
        requiredFields.push(
            { field: trust.name, property: 'name', error: "Name is required." },
        );
    }

    requiredFields.forEach(({ field, property, error }) => {
        const validationError = validateRequiredField(field, property, trust.trustId, error);
        if (validationError) validationErrors.push(validationError);
    });


    if(enableEstateFlowchartMVPRelease) {
        let flowchartNameDuplicateError;
        const element = document.getElementById("form-header");
        const searchText = "Add";
        const equalsOtherFlowchartNames = () => {
            return estateFlowcharts.some(fc => {
                if (flowchartId !== undefined && flowchartId !== fc.flowchartId) {
                    return fc.flowchartName?.toLowerCase() === trust.flowchartName.toLowerCase();
                }
                return false;
            });
        }
        const getDuplicateError = () => {
            return validateDuplicateField(estateFlowcharts, trust.flowchartName, 'flowchartName', trust.trustId, "Flowchart Name already exists.");
        }

        if (element && (element.textContent || element.innerText).includes(searchText)) {
            if(flowchartId === 'new') {
                //New Flowchart
                flowchartNameDuplicateError = getDuplicateError();
            } else {
                if (equalsOtherFlowchartNames()) {
                    //New Estate Item to existing flowchart, only validate if name belongs to some other flowchart
                    flowchartNameDuplicateError = getDuplicateError();
                }
            }
        } else {
            if (equalsOtherFlowchartNames()) {
                // Editing existing flowchart's item, only validate if name belongs to some other flowchart
                flowchartNameDuplicateError = getDuplicateError();
            }
        }
        if (flowchartNameDuplicateError) validationErrors.push(flowchartNameDuplicateError);
    }

    return validationErrors;
};

// Helper for grantors/trustees required
function getAdditionalRequiredValidations(trust: EstateItemTypes): ValidationError[] {
    const errors: ValidationError[] = [];
    if ('grantors' in trust && !trust.grantors?.length) {
        errors.push({
            type: ValidationErrorType.REQUIRED_FIELDS,
            property: 'grantors',
            parent: trust.trustId,
            error: "Grantors is required."
        });
    }
    if ('trustees' in trust && !trust.trustees?.length) {
        errors.push({
            type: ValidationErrorType.REQUIRED_FIELDS,
            property: 'trustees',
            parent: trust.trustId,
            error: "Trustees is required."
        });
    }
    return errors;
}

// Helper for powersOfAppointment validation
function getPoaValidations(trust: IrrevocableTrust): ValidationError[] {
    const errors: ValidationError[] = [];
    trust.powersOfAppointment?.forEach((poa) => {
            if (!isValid(poa.powerHolder)) errors.push({
                type: ValidationErrorType.REQUIRED_FIELDS,
                property: 'powerHolder',
                parent: trust.trustId,
                error: "Power Holder is required."
            });
        })
    return errors;
}


// Helper for IrrevocableTrust-specific validations
function getIrrevocableTrustValidations(trust: IrrevocableTrust): ValidationError[] {
    const errors: ValidationError[] = [];
    if (trust.terminatesUponSpecificDeath && !trust.firstDeathTerminationMember?.memberId) {
        errors.push({
            type: ValidationErrorType.REQUIRED_FIELDS,
            property: 'firstDeathTerminationMember',
            parent: trust.trustId,
            error: "Whose death? is required.",
        });
    }
    errors.push(...getPoaValidations(trust));
    
    const requiredDistributionsError = validateRequiredField(trust.distributions, 'distributions', trust.trustId, "Distributions is required.");
    if (requiredDistributionsError) errors.push(requiredDistributionsError);
    return errors;
}

export const validateBasicInformation = (trust: EstateItemTypes, flowcharts: EstateFlowchartState[], flowchartId: string, enableEstateFlowchartMVPRelease: boolean): ValidationError[] => {
    let validationErrors = validateBasicDetailsSection(trust, flowcharts, flowchartId, enableEstateFlowchartMVPRelease);
    validationErrors = [
        ...validationErrors,
        ...getAdditionalRequiredValidations(trust)
    ];

    if (isIrrevocableTrust(trust)) {
        validationErrors = [
            ...validationErrors,
            ...getIrrevocableTrustValidations(trust)
        ];
    }

    if (isWill(trust)) {
        validationErrors = [
            ...validationErrors,
        ];
    }

    return validationErrors;
};